/**
 * The Gather motif's geometry (#14364; motif decided in #14358).
 *
 * `gatherScene(stage, t, size)` returns the shapes one frame paints, `t`
 * milliseconds into the stage. The canvas in `gather-motif.tsx` only paints
 * them, so this module is where the motif is defined and tested:
 *
 *   - UPLOADING / TRANSCRIBING: word-dashes are written row by row across the
 *     left of the panel, like text being typed, and scroll up once the rows
 *     fill.
 *   - SUMMARIZING: dashes lift off the rows and fly into a three-line summary
 *     block on the right. The block grows as they land. A lifted dash is
 *     rewritten a few seconds later, so the flow keeps going however long the
 *     pass takes, and the block keeps approaching full.
 *   - EXTRACTING: accent chips pop out beneath the finished block, one at a
 *     time.
 *   - DONE: the dashes fade and the block settles into solid lines.
 *
 * With `opening` (#14384), UPLOADING / TRANSCRIBING first draw ONE flat line
 * across the first row, hold it, then break it into that row's word-dashes
 * and write on from the second row. That line is where the recording bar's
 * settled waveform lands when Stop opens the note: `gather-motif.tsx` puts a
 * DOM twin of it (`GATHER_START_LINE`) under the shared
 * `RECORDING_LINE_VIEW_TRANSITION_NAME`, since a canvas cannot carry one.
 *
 * Every shape is computed from `t` and a deterministic hash. Nothing is
 * stored between frames, so the cost of a frame does not grow the longer a
 * run takes. With `still`, the frame has nothing in flight. That is the
 * reduced-motion frame, drawn once per stage.
 *
 * Pure: no DOM, no clock.
 */

export type GatherStage =
  | 'UPLOADING'
  | 'TRANSCRIBING'
  | 'SUMMARIZING'
  | 'EXTRACTING'
  | 'DONE'

/**
 * `dash`: a word on the rows. `flight`: a dash on its way to the block.
 * `line`: one of the summary block's lines. `chip`: an item.
 */
export type GatherShape = {
  kind: 'dash' | 'flight' | 'line' | 'chip'
  x: number
  y: number
  w: number
  h: number
  alpha: number
  /** Which semantic color paints it; `gather-motif.tsx` maps these. */
  tone: 'dash' | 'ink' | 'accent'
}

export type GatherSize = { width: number; height: number }

export const GATHER_BLOCK_LINES = 3

const PAD = 12
const ROW_PITCH = 14
const DASH_H = 4
const DASH_GAP = 5
/** How long one row takes to write. */
const ROW_MS = 1400
/** How long the scroll-up takes when a new row starts on a full panel. */
const SCROLL_MS = 240
/** A newly written dash fades in over this long. */
const DASH_FADE_MS = 120

/** One dash lifts off the rows this often while summarizing. */
const LAUNCH_MS = 140
/** How long a dash takes to fly to the block. */
const FLIGHT_MS = 700
/** A lifted dash is rewritten on its row this long after it left. */
const REWRITE_MS = 4000
/** How high a flight arcs above its straight path. */
const FLIGHT_ARC = 18

const LINE_H = 6
const LINE_PITCH = 14
/** The block's last line is shorter, as a paragraph's is. */
const LINE_SHARES = [1, 1, 0.62] as const

/** One chip pops out this often while finding items. */
const CHIP_MS = 800
const CHIP_POP_MS = 220
const MAX_CHIPS = 4
const CHIP_H = 10
const CHIP_GAP = 6

/** Dashes fade out over this long once the notes are done. */
const DONE_FADE_MS = 480

/**
 * The shared-element name the recording bar's settled waveform and the
 * motif's start line both carry, so Stop's navigation morphs one into the
 * other (#14384). ONE constant for both ends: a typo in either would leave
 * two unpaired names and no morph, with nothing failing.
 */
export const RECORDING_LINE_VIEW_TRANSITION_NAME = 'recording-line'

/**
 * How long the opening holds the flat line before it breaks. Longer than
 * the morph (`--motion-duration-base`) plus the DOM twin's fade
 * (`--motion-duration-fast`), so the twin is gone before the canvas's line
 * under it starts to move.
 */
export const GATHER_OPEN_HOLD_MS = 480
/** How long the flat line takes to break into the first row's dashes. */
export const GATHER_OPEN_BREAK_MS = 480

/**
 * Where the opening's flat line sits, in CSS pixels of the canvas box: the
 * first row, from the left padding to the right edge of the writing area.
 * `gather-motif.tsx` positions the line's DOM twin from these.
 */
export const GATHER_START_LINE = {
  left: PAD,
  top: PAD,
  height: DASH_H,
  /** The right end, as a share of the canvas width. */
  rightShare: 0.52,
} as const

/** A small integer hash, so every frame agrees on every dash's width. */
function hash(n: number): number {
  let x = (n | 0) ^ 0x9e3779b9
  x = Math.imul(x ^ (x >>> 16), 0x85ebca6b)
  x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35)
  return (x ^ (x >>> 16)) >>> 0
}

function easeInOut(p: number): number {
  return p < 0.5 ? 4 * p * p * p : 1 - (-2 * p + 2) ** 3 / 2
}

function easeOut(p: number): number {
  return 1 - (1 - p) ** 3
}

const clamp01 = (p: number) => Math.min(1, Math.max(0, p))

type Geometry = {
  left: number
  right: number
  top: number
  visibleRows: number
  blockLeft: number
  blockRight: number
}

function geometry({ width, height }: GatherSize): Geometry {
  return {
    left: PAD,
    right: width * GATHER_START_LINE.rightShare,
    top: PAD,
    visibleRows: Math.max(1, Math.floor((height - 2 * PAD) / ROW_PITCH)),
    blockLeft: width * 0.6,
    blockRight: width - PAD,
  }
}

/** Row `k`'s dashes, left to right: `[x, w]` pairs. */
function rowDashes(k: number, g: Geometry): Array<[number, number]> {
  const out: Array<[number, number]> = []
  let x = g.left
  for (let j = 0; j < 64; j++) {
    const w = 8 + (hash(k * 131 + j) % 26)
    if (x + w > g.right) break
    out.push([x, w])
    x += w + DASH_GAP
  }
  return out
}

function inside(shape: GatherShape, size: GatherSize): boolean {
  return shape.y >= 0 && shape.y + shape.h <= size.height
}

/** Transcribing: row by row, scrolling once the panel is full. */
function writingRows(t: number, g: Geometry, size: GatherSize): GatherShape[] {
  const current = Math.floor(t / ROW_MS)
  const inRow = t - current * ROW_MS
  const fraction = inRow / ROW_MS
  const first = Math.max(0, current - g.visibleRows + 1)
  const scrolling = current >= g.visibleRows
  const offset = scrolling
    ? (1 - easeOut(clamp01(inRow / SCROLL_MS))) * ROW_PITCH
    : 0

  const shapes: GatherShape[] = []
  for (let k = first; k <= current; k++) {
    const y = g.top + (k - first) * ROW_PITCH + offset
    const span = g.right - g.left
    for (const [x, w] of rowDashes(k, g)) {
      let alpha = 1
      if (k === current) {
        const at = (x + w - g.left) / span
        if (at > fraction) break
        const writtenAt = at * ROW_MS
        alpha = clamp01((inRow - writtenAt) / DASH_FADE_MS)
      }
      const dash: GatherShape = {
        kind: 'dash',
        x,
        y,
        w,
        h: DASH_H,
        alpha,
        tone: 'dash',
      }
      if (inside(dash, size)) shapes.push(dash)
    }
  }
  return shapes
}

/**
 * The opening: one flat line, then the first row's dashes opening out of it.
 * Each dash starts as a tile of the line — the tiles cover it end to end —
 * and eases to its written place, so the gaps open where the words will be.
 * After it, the writing carries on as if the first row had just been
 * written.
 */
function openingRows(t: number, g: Geometry, size: GatherSize): GatherShape[] {
  if (t >= GATHER_OPEN_HOLD_MS + GATHER_OPEN_BREAK_MS) {
    return writingRows(
      t - GATHER_OPEN_HOLD_MS - GATHER_OPEN_BREAK_MS + ROW_MS,
      g,
      size,
    )
  }
  const flat = (x: number, w: number): GatherShape => ({
    kind: 'dash',
    x,
    y: g.top,
    w,
    h: DASH_H,
    alpha: 1,
    tone: 'dash',
  })
  const row = rowDashes(0, g)
  if (t < GATHER_OPEN_HOLD_MS || row.length === 0) {
    return [flat(g.left, g.right - g.left)]
  }
  const p = easeOut(clamp01((t - GATHER_OPEN_HOLD_MS) / GATHER_OPEN_BREAK_MS))
  const [lastX, lastW] = row[row.length - 1]
  const stretch = (g.right - g.left) / (lastX + lastW + DASH_GAP - g.left)
  const tileStart = (x: number) => g.left + (x - g.left) * stretch
  return row.map(([x, w], j) => {
    const from = tileStart(x)
    const to = j + 1 < row.length ? tileStart(row[j + 1][0]) : g.right
    const start = from + (x - from) * p
    const end = to + (x + w - to) * p
    return flat(start, end - start)
  })
}

/** The full, static grid the gathering stages draw from. */
function grid(g: Geometry): Array<{ x: number; y: number; w: number }> {
  const cells: Array<{ x: number; y: number; w: number }> = []
  for (let k = 0; k < g.visibleRows; k++) {
    for (const [x, w] of rowDashes(1000 + k, g)) {
      cells.push({ x, y: g.top + k * ROW_PITCH, w })
    }
  }
  return cells
}

/** Where each launch comes from: a fixed shuffle of the grid. */
function launchOrder(count: number): number[] {
  return Array.from({ length: count }, (_, i) => i).sort(
    (a, b) => hash(a + 7) - hash(b + 7),
  )
}

function blockLines(fill: number, g: Geometry, alpha: number): GatherShape[] {
  const full = g.blockRight - g.blockLeft
  const widths = LINE_SHARES.map(share => share * full)
  const capacity = widths.reduce((sum, w) => sum + w, 0)
  let remaining = clamp01(fill) * capacity
  const shapes: GatherShape[] = []
  widths.forEach((lineWidth, i) => {
    const w = Math.min(lineWidth, remaining)
    remaining -= w
    shapes.push({
      kind: 'line',
      x: g.blockLeft,
      y: g.top + 4 + i * LINE_PITCH,
      w: Math.max(w, 0),
      h: LINE_H,
      alpha,
      tone: 'ink',
    })
  })
  return shapes
}

function blockBottom(g: Geometry): number {
  return g.top + 4 + (GATHER_BLOCK_LINES - 1) * LINE_PITCH + LINE_H
}

/** Summarizing: dashes lift off the grid and land in the block. */
function gathering(t: number, g: Geometry, still: boolean): GatherShape[] {
  const cells = grid(g)
  const order = launchOrder(cells.length)
  const launched = Math.floor(t / LAUNCH_MS)
  const shapes: GatherShape[] = []

  // The grid, minus the dashes that lifted off recently.
  order.forEach((cellIndex, position) => {
    const cell = cells[cellIndex]
    let alpha = 1
    if (launched >= position && cells.length > 0) {
      const lastLaunch =
        position +
        Math.floor((launched - position) / cells.length) * cells.length
      const since = t - lastLaunch * LAUNCH_MS
      alpha = since < REWRITE_MS ? 0 : clamp01((since - REWRITE_MS) / 300)
    }
    shapes.push({
      kind: 'dash',
      x: cell.x,
      y: cell.y,
      w: cell.w,
      h: DASH_H,
      alpha,
      tone: 'dash',
    })
  })

  // The block approaches full as dashes land, and never quite overshoots.
  const landed = Math.max(0, Math.floor((t - FLIGHT_MS) / LAUNCH_MS) + 1)
  const fill = 1 - Math.exp(-landed / Math.max(1, cells.length * 0.6))
  shapes.push(...blockLines(fill, g, 0.8))

  if (!still && cells.length > 0) {
    const bottom = blockBottom(g)
    for (let f = Math.max(0, launched - 8); f <= launched; f++) {
      const progress = (t - f * LAUNCH_MS) / FLIGHT_MS
      if (progress < 0 || progress >= 1) continue
      const cell = cells[order[f % cells.length]]
      const lane = hash(f) % GATHER_BLOCK_LINES
      const tx =
        g.blockLeft +
        ((hash(f + 3) % 100) / 100) * (g.blockRight - g.blockLeft - cell.w)
      const ty = g.top + 4 + lane * LINE_PITCH
      const p = easeInOut(progress)
      const y = cell.y + (ty - cell.y) * p - Math.sin(Math.PI * p) * FLIGHT_ARC
      shapes.push({
        kind: 'flight',
        x: cell.x + (tx - cell.x) * p,
        y: Math.min(Math.max(0, y), bottom),
        w: cell.w * (1 - 0.3 * p),
        h: DASH_H,
        alpha: 1,
        tone: 'dash',
      })
    }
  }
  return shapes
}

function chipsAt(t: number, g: Geometry): GatherShape[] {
  const count = Math.min(MAX_CHIPS, Math.floor(t / CHIP_MS))
  const available = g.blockRight - g.blockLeft
  const chipW = Math.min(
    30,
    (available - (MAX_CHIPS - 1) * CHIP_GAP) / MAX_CHIPS,
  )
  const y = blockBottom(g) + 10
  const shapes: GatherShape[] = []
  for (let i = 0; i < count; i++) {
    const pop = easeOut(clamp01((t - (i + 1) * CHIP_MS) / CHIP_POP_MS))
    const scale = 0.3 + 0.7 * pop
    const w = chipW * scale
    const h = CHIP_H * scale
    const cx = g.blockLeft + i * (chipW + CHIP_GAP) + chipW / 2
    shapes.push({
      kind: 'chip',
      x: cx - w / 2,
      y: y + (CHIP_H - h) / 2,
      w,
      h,
      alpha: pop,
      tone: 'accent',
    })
  }
  return shapes
}

/** The shapes of one frame, `t` ms into `stage`. See the module header. */
export function gatherScene(
  stage: GatherStage,
  t: number,
  size: GatherSize,
  options: { still?: boolean; opening?: boolean } = {},
): GatherShape[] {
  const g = geometry(size)
  const at = Math.max(0, t)
  switch (stage) {
    case 'UPLOADING':
    case 'TRANSCRIBING':
      return options.opening === true && options.still !== true
        ? openingRows(at, g, size)
        : writingRows(at, g, size)
    case 'SUMMARIZING':
      return gathering(at, g, options.still === true)
    case 'EXTRACTING': {
      const faint = grid(g).map<GatherShape>(cell => ({
        kind: 'dash',
        x: cell.x,
        y: cell.y,
        w: cell.w,
        h: DASH_H,
        alpha: 0.35,
        tone: 'dash',
      }))
      return [...faint, ...blockLines(1, g, 0.8), ...chipsAt(at, g)]
    }
    case 'DONE': {
      const fade = 1 - clamp01(at / DONE_FADE_MS)
      const dashes = grid(g).map<GatherShape>(cell => ({
        kind: 'dash',
        x: cell.x,
        y: cell.y,
        w: cell.w,
        h: DASH_H,
        alpha: 0.35 * fade,
        tone: 'dash',
      }))
      return [
        ...dashes,
        ...blockLines(1, g, 1),
        ...chipsAt(CHIP_MS * MAX_CHIPS + CHIP_POP_MS, g),
      ]
    }
  }
}

/**
 * The moment the reduced-motion still frame shows for each stage: a few rows
 * written, a half-gathered block, two chips, the settled block.
 */
export function gatherStillTime(stage: GatherStage): number {
  switch (stage) {
    case 'UPLOADING':
    case 'TRANSCRIBING':
      return ROW_MS * 2.6
    case 'SUMMARIZING':
      return FLIGHT_MS + LAUNCH_MS * 20
    case 'EXTRACTING':
      return CHIP_MS * 2 + CHIP_POP_MS
    case 'DONE':
      return DONE_FADE_MS
  }
}
